import React, { useState, useEffect, useContext, useRef } from "react";
import getAjax from '../js/utilities/getAjax';
import { GENERAL_URL_API, ASSETS_URL } from '../components/Store.js';
import LibroColeccion from "../coleccion/LibroColeccion";
import { cambiarSaltoLinea } from "../js/functions/cambiarSaltoLinea";
import { useParams } from "react-router-dom";
import SkeletonFichaEditorial from './../skeletons/SkeletonFichaEditorial';
import useObtenerTraducciones from "../js/translations/useObtenerTraducciones";
import { AccesibilidadContext } from "../contexts/AccesibilidadContext";

import { Helmet } from "react-helmet";
import { useId } from "../hooks/use-id.js";

/**
 * Componente que muestra la ficha de una editorial, en donde se muestra información de la editorial y un listado de libros que están vinculados.
 *
 * @component
 * @example
 * return <FichaEditorial />
 */
const FichaEditorial = () => {
  const [isLoading, setIsloading] = useState(true);
  const [datosEditorial, setDatosEditorial] = useState('');
  const [currenTab, setCurrentTab] = useState('resena')
  const accesibilidadState = useContext(AccesibilidadContext);
  const [books, setBooks] = useState([]);

  const { nombreEditorial } = useParams();

  const { isReady, textosSeccion: textoEditorial } = useObtenerTraducciones('ficha_editorial');

  const panelRef = useRef();

  const tabResenaId = useId('tab-resena');
  const panelPremios = useId('panel-premios');
  const tabPremiosId = useId('tab-premios');
  const panelResenaId = useId('panel-resena');

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.focus();
    }
  }, [currenTab]);

  useEffect(() => {
    /*let nombreColeccion = id.split('-').join(' ');
    let capitalizeNombre = nombreColeccion.charAt(0).toUpperCase() + nombreColeccion.slice(1);*/

    getAjax(`${GENERAL_URL_API}/assets/datosEditorial`, { nombreEditorial: nombreEditorial }, response => {
      if (response === 'Error, editoral inexistente') {
        return new Error(response);
      } else {
        setDatosEditorial(JSON.parse(response));
        const libros = Array.isArray(JSON.parse(response).libros) ? JSON.parse(response).libros : [];
        // evitar libros duplicados.
        const ids = new Set();
        const filteredBooks = libros.filter(book => {
          if (ids.has(book.idLibro)) return false;
          ids.add(book.idLibro);
          return true;
        });
        setBooks(filteredBooks);
        setIsloading(false);
      }

    })

  }, [nombreEditorial])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') setCurrentTab('premios');
      if (e.key === 'ArrowLeft') setCurrentTab('resena');
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <Helmet>
        <title>{`MakeMake - ${datosEditorial.nombreEditorial}`}</title>
        <meta name="description" content={datosEditorial.descripcion !== null ? datosEditorial.descripcion : 'descripción editorial'} />
      </Helmet>

      {(() => {
        if (isLoading) {
          return (
            <div role="status" aria-live="polite">
              <SkeletonFichaEditorial />
            </div>
          )
        } else {
          return (
            <>
              {isReady &&
                <div className="main-container">
                  <div className={`${accesibilidadState.temaActual === 'alto-contraste' && 'background--bg '} coleccion-container`} style={{ backgroundColor: accesibilidadState.temaActual === 'clasico' && datosEditorial.color1 }}>
                    <div className={`${accesibilidadState.temaActual === 'alto-contraste' && 'background--bg '} coleccion-background coleccion-editorial`} style={{ background: `${accesibilidadState.temaActual === 'clasico' && datosEditorial.color2} url('/img/background.png') 50%/cover fixed repeat` }}></div>
                    <div className="container pb-lg-5">
                      <div className="row pt-5 mb-5">
                        <div className="col-lg-6 mb-5 mb-lg-0">
                          <h1 tabIndex={0} className={`focusable-secondary ${accesibilidadState.temaActual === 'alto-contraste' && 'onbackground-primary--color'}`} style={{ color: accesibilidadState.temaActual === 'clasico' && datosEditorial.colorLetra }}>{datosEditorial.nombreEditorial}</h1>
                          <div className="row justify-content-end mt-4 mx-0">
                            <a className="focusable-secondary" href={datosEditorial.linkEditorial} target="_blank" rel="noopener noreferrer" aria-label={`${textoEditorial?.enlace_label1 || "Abrir sitio web de"} ${datosEditorial.nombreEditorial} ${textoEditorial?.enlace_label2 || "en una nueva pestaña"}`}>
                              <img alt={`Logo de la editorial '${datosEditorial.nombreEditorial}'`} style={{ width: '100%', maxWidth: 270 }} src={`${ASSETS_URL}${datosEditorial.logo}`} />
                            </a>
                          </div>
                        </div>
                        <div className="col-lg-6 pl-lg-5 ">
                          <ul role="tablist" aria-label={textoEditorial?.tablist || "Información de la editorial"} className="row nav-menu p-0 m-0 menu-tabs nav-menu-noresponsive">
                            <li>
                              <button
                                role="tab"
                                id={tabResenaId}
                                aria-selected={currenTab === 'resena'}
                                aria-controls={panelResenaId}
                                tabIndex={currenTab === 'resena' ? 0 : -1}
                                className={`w-100 h-100 focusable-secondary px-4 py-2 mr-3 ${currenTab === 'resena' ? 'nav-menu--active' : ''}`}
                                data-target="editorial-resena"
                                onClick={() => setCurrentTab('resena')}
                              >
                                {textoEditorial?.resena || 'Reseña'}
                              </button>
                            </li>

                            {datosEditorial.premios !== '' && datosEditorial.premios !== null &&
                              <li role={"tab"}>
                                <button
                                  role="tab"
                                  id={tabPremiosId}
                                  aria-selected={currenTab === 'premios'}
                                  aria-controls={panelPremios}
                                  tabIndex={currenTab === 'premios' ? 0 : -1}
                                  className={`w-100 h-100 focusable-secondary px-4 py-2 ${currenTab === 'premios' ? 'nav-menu--active' : ''}`}
                                  data-target="editorial-premios"
                                  onClick={() => setCurrentTab('premios')}
                                >
                                  {textoEditorial?.premios || 'Premios'}
                                </button>
                              </li>
                            }
                          </ul>
                          <div className="fichalibro-data fichalibro-data--text fichaeditorial p-4 p-sm-3 p-lg-5">

                            {(() => {
                              if (currenTab === 'resena') {
                                return (
                                  <div
                                    role="tabpanel"
                                    id={panelResenaId}
                                    aria-labelledby={tabResenaId}
                                    hidden={currenTab !== 'resena'}
                                    className="data--tab tab-active pr-4"
                                    ref={panelRef}
                                    tabIndex={-1}
                                  >
                                    <div className="container-tabs">
                                      {(() => {
                                        if (datosEditorial.descripcion !== null) {
                                          return (
                                            <>
                                              {cambiarSaltoLinea("\n", datosEditorial.descripcion)}
                                            </>
                                          )
                                        } else {
                                          return ""
                                        }
                                      })()}
                                      <div className="row justify-content-end mt-4 mx-0">
                                        <a href={datosEditorial.linkEditorial} rel="noopener noreferrer" target="_blank" className="focusable-secondary web-page btn btn-light px-4 mt-3">
                                          {textoEditorial?.web || 'Página web'} <i className="fas fa-external-link-alt ml-2" aria-hidden={true}></i>
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                )
                              } else {
                                return (
                                  <div
                                    role="tabpanel"
                                    id={panelPremios}
                                    aria-labelledby={tabPremiosId}
                                    hidden={currenTab !== 'premios'}
                                    className="data--tab tab-active pr-4"
                                  >
                                    <div className="container-tabs">
                                      {cambiarSaltoLinea("\n", datosEditorial.premios)}
                                    </div>
                                  </div>
                                )
                              }
                            })()}

                          </div>
                        </div>
                      </div>
                      <div className="mt-5 pb-5 mt-lg-0 align-items-end">
                        <ul aria-label="Libros" className="focusable-secondary row">
                          {books?.map((book, i) => {
                            return (
                              <li key={book?.idLibro || i} className="col-6 col-sm-4 col-md-3 col-lg-2 mb-4">
                                <LibroColeccion
                                  clase={"w-100"}
                                  item={book}
                                />
                              </li>

                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </>
          )
        }
      })()}
    </>
  )
}

export default FichaEditorial;